import { useState, FormEvent, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL } from '../../../lib/businessInfo';
import { useToast } from '../../../components/base/Toast';
import { getBrokerInquirySchema } from '../../../schemas/brokerInquirySchema';
import { preloadZod } from '../../../lib/zodLoader';

const BODY_TYPES = ['Sedan', 'SUV', 'Truck', 'Coupe', 'Convertible', 'Van', 'Wagon', 'Hatchback'];
const BUDGETS = ['Under $15,000', '$15,000–$25,000', '$25,000–$35,000', '$35,000–$50,000', '$50,000+'];
const TIMELINES = ['ASAP', 'Within 30 days', '1–3 months', 'Just exploring'];
const YEARS = Array.from({ length: 2027 - 1995 + 1 }, (_, i) => 2027 - i);
const TRIM_LEVELS = ['Base / L', 'LE / LX', 'SE / SV', 'EX / XLE', 'Limited', 'Sport', 'Touring', 'Platinum / Premium', 'TRD / Off-Road', 'Other'];

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface BrokerInquiryFormProps {
  defaultMake?: string;
  defaultModel?: string;
}

export default function BrokerInquiryForm({ defaultMake = '', defaultModel = '' }: BrokerInquiryFormProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const errorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const toast = useToast();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const form = e.currentTarget;
    const data = new FormData(form);

    // Clear previous errors
    setValidationErrors({});

    // Build payload object
    const payload: Record<string, string> = {};
    data.forEach((value, key) => {
      payload[key] = value.toString();
    });

    // Validate with lazy-loaded Zod schema
    const schema = await getBrokerInquirySchema();
    const result = schema.safeParse(payload);

    if (!result.success) {
      const errors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const path = err.path.join('.');
        errors[path] = err.message;
      });
      setValidationErrors(errors);

      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(`broker-${firstErrorField}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }

    setStatus('submitting');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/broker-inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        // Google Ads conversion tracking — Broker Inquiry Lead
        if (typeof window.trackConversion === 'function') {
          window.trackConversion('BROKER_INQUIRY_LABEL', 50.0);
        }
        // GA4 lead event
        if (typeof window.trackLead === 'function') {
          window.trackLead('Broker Inquiry Form');
        }
        navigate('/thank-you?from=broker');
      } else {
        setStatus('error');
        // Move focus to the error live region
        requestAnimationFrame(() => errorRef.current?.focus());
        toast.error('Failed to submit inquiry. Please try again or call us at (855) 468-6348.');
      }
    } catch {
      setStatus('error');
      // Move focus to the error live region
      requestAnimationFrame(() => errorRef.current?.focus());
      toast.error('Something went wrong. Please try again or call us at (855) 468-6348.');
    } finally {
      if (status !== 'success') setStatus('idle');
    }
  };

  return (
    <div id="broker-inquiry-form" className="bg-white rounded-2xl border border-[#E8E2D6] shadow-sm overflow-hidden">
      {/* Form Header */}
      <div className="bg-[#1C2B1E] px-8 py-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8B84B] rounded-full">
            <i className="ri-search-eye-line text-[#1C2B1E] text-xl"></i>
          </div>
          <div>
            <h3 className="font-['Oswald'] text-xl font-bold text-white">Tell Us What You Want</h3>
            <p className="text-white/85 text-sm">No credit card. No commitment. Just tell us what you want.</p>
          </div>
        </div>
      </div>

      <form
        id="broker-inquiry-form-el"
        data-readdy-form
        onSubmit={handleSubmit}
        onMouseEnter={() => preloadZod()}
        onFocus={() => preloadZod()}
        className="p-8 space-y-6"
      >
        {/* Server / network error live region */}
        {status === 'error' && (
          <div
            ref={errorRef}
            tabIndex={-1}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 focus:outline-none"
          >
            <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0 mt-0.5" aria-hidden="true"></i>
            <p className="text-red-700 text-sm">
              Something went wrong. Please try again or call us at{' '}
              <a href={`tel:${PHONE_TEL}`} className="font-semibold underline">{PHONE_DISPLAY}</a>.
            </p>
          </div>
        )}

        {/* Name + Email */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="broker-name" className="block text-sm font-semibold text-[#1C2B1E] mb-1.5">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="broker-name"
              name="name"
              required
              autoComplete="name"
              aria-required="true"
              aria-invalid={!!validationErrors.name}
              aria-describedby={validationErrors.name ? 'broker-name-error' : undefined}
              placeholder="John Smith"
              className={`w-full border rounded-lg px-4 py-3 text-sm text-[#1C2B1E] placeholder-gray-400 focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition ${
                validationErrors.name ? 'border-red-500' : 'border-[#E8E2D6]'
              }`}
            />
            {validationErrors.name && (
              <p id="broker-name-error" className="text-red-600 text-sm mt-1" role="alert">
                {validationErrors.name}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="broker-email" className="block text-sm font-semibold text-[#1C2B1E] mb-1.5">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="broker-email"
              name="email"
              required
              autoComplete="email"
              aria-required="true"
              aria-invalid={!!validationErrors.email}
              aria-describedby={validationErrors.email ? 'broker-email-error' : undefined}
              placeholder="john@example.com"
              className={`w-full border rounded-lg px-4 py-3 text-sm text-[#1C2B1E] placeholder-gray-400 focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition ${
                validationErrors.email ? 'border-red-500' : 'border-[#E8E2D6]'
              }`}
            />
            {validationErrors.email && (
              <p id="broker-email-error" className="text-red-600 text-sm mt-1" role="alert">
                {validationErrors.email}
              </p>
            )}
          </div>
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="broker-phone" className="block text-sm font-semibold text-[#1C2B1E] mb-1.5">Phone Number</label>
          <input
            type="tel"
            id="broker-phone"
            name="phone"
            autoComplete="tel"
            aria-invalid={!!validationErrors.phone}
            aria-describedby={validationErrors.phone ? 'broker-phone-error' : undefined}
            placeholder="(503) 555-0100"
            className={`w-full border rounded-lg px-4 py-3 text-sm text-[#1C2B1E] placeholder-gray-400 focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition ${
              validationErrors.phone ? 'border-red-500' : 'border-[#E8E2D6]'
            }`}
          />
          {validationErrors.phone && (
            <p id="broker-phone-error" className="text-red-600 text-sm mt-1" role="alert">
              {validationErrors.phone}
            </p>
          )}
        </div>

        {/* Make / Model / Year */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label htmlFor="broker-make" className="block text-sm font-semibold text-[#1C2B1E] mb-1.5">Preferred Make</label>
            <input
              type="text"
              id="broker-make"
              name="preferred_make"
              placeholder="e.g. Toyota"
              defaultValue={defaultMake}
              key={`make-${defaultMake}`}
              className="w-full border border-[#E8E2D6] rounded-lg px-4 py-3 text-sm text-[#1C2B1E] placeholder-gray-400 focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition"
            />
          </div>
          <div>
            <label htmlFor="broker-model" className="block text-sm font-semibold text-[#1C2B1E] mb-1.5">Preferred Model</label>
            <input
              type="text"
              id="broker-model"
              name="preferred_model"
              placeholder="e.g. Tacoma"
              defaultValue={defaultModel}
              key={`model-${defaultModel}`}
              className="w-full border border-[#E8E2D6] rounded-lg px-4 py-3 text-sm text-[#1C2B1E] placeholder-gray-400 focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition"
            />
          </div>
          <div>
            <p className="block text-sm font-semibold text-[#1C2B1E] mb-1.5" id="broker-year-range-label">Year Range</p>
            <div className="flex items-center gap-2">
              <div className="w-full">
                <label htmlFor="broker-year-from" className="sr-only">Year from</label>
                <select
                  id="broker-year-from"
                  name="year_from"
                  aria-labelledby="broker-year-range-label"
                  className="w-full border border-[#E8E2D6] rounded-lg px-3 py-3 text-sm text-[#1C2B1E] focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition bg-white cursor-pointer"
                >
                  <option value="">From</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <span className="text-[#4A5568] text-sm flex-shrink-0" aria-hidden="true">–</span>
              <div className="w-full">
                <label htmlFor="broker-year-to" className="sr-only">Year to</label>
                <select
                  id="broker-year-to"
                  name="year_to"
                  aria-labelledby="broker-year-range-label"
                  className="w-full border border-[#E8E2D6] rounded-lg px-3 py-3 text-sm text-[#1C2B1E] focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition bg-white cursor-pointer"
                >
                  <option value="">To</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Body Type */}
        <div>
          <label className="block text-sm font-semibold text-[#1C2B1E] mb-2" id="broker-body-type-label">
            Body Type
            <span className="ml-2 text-xs font-normal text-[#4A5568]" id="broker-body-type-hint">(Optional — select one)</span>
          </label>
          <div
            className="flex flex-wrap gap-2"
            role="radiogroup"
            aria-labelledby="broker-body-type-label"
            aria-describedby="broker-body-type-hint"
          >
            {BODY_TYPES.map((type) => (
              <label key={type} className="cursor-pointer">
                <input type="radio" name="body_type" value={type} className="sr-only peer" />
                <span className="inline-block px-4 py-2 rounded-full border border-[#E8E2D6] text-sm font-medium text-gray-600 peer-checked:bg-[#E8B84B] peer-checked:border-[#E8B84B] peer-checked:text-[#1C2B1E] hover:border-[#E8B84B] transition cursor-pointer whitespace-nowrap">
                  {type}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Trim Level */}
        <div>
          <label className="block text-sm font-semibold text-[#1C2B1E] mb-2" id="broker-trim-label">
            Preferred Trim Level
            <span className="ml-2 text-xs font-normal text-[#4A5568]" id="broker-trim-hint">(Optional — select one)</span>
          </label>
          <div
            className="flex flex-wrap gap-2"
            role="radiogroup"
            aria-labelledby="broker-trim-label"
            aria-describedby="broker-trim-hint"
          >
            {TRIM_LEVELS.map((trim) => (
              <label key={trim} className="cursor-pointer">
                <input type="radio" name="preferred_trim" value={trim} className="sr-only peer" />
                <span className="inline-block px-4 py-2 rounded-full border border-[#E8E2D6] text-sm font-medium text-gray-600 peer-checked:bg-[#E8B84B] peer-checked:border-[#E8B84B] peer-checked:text-[#1C2B1E] hover:border-[#E8B84B] transition cursor-pointer whitespace-nowrap">
                  {trim}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Preferred Color */}
        <div>
          <label htmlFor="broker-color" className="block text-sm font-semibold text-[#1C2B1E] mb-1.5">
            Preferred Color
            <span className="ml-2 text-xs font-normal text-[#4A5568]">(Optional)</span>
          </label>
          <input
            type="text"
            id="broker-color"
            name="preferred_color"
            placeholder="e.g. White, Silver, Midnight Black..."
            className="w-full border border-[#E8E2D6] rounded-lg px-4 py-3 text-sm text-[#1C2B1E] placeholder-gray-400 focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition"
          />
        </div>

        {/* Budget + Timeline */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="broker-budget" className="block text-sm font-semibold text-[#1C2B1E] mb-1.5">
              Budget <span className="text-red-500">*</span>
            </label>
            <select
              id="broker-budget"
              name="budget"
              required
              aria-required="true"
              aria-invalid={!!validationErrors.budget}
              aria-describedby={validationErrors.budget ? 'broker-budget-error' : undefined}
              className={`w-full border rounded-lg px-4 py-3 text-sm text-[#1C2B1E] focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition bg-white cursor-pointer ${
                validationErrors.budget ? 'border-red-500' : 'border-[#E8E2D6]'
              }`}
            >
              <option value="">Select budget range</option>
              {BUDGETS.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            {validationErrors.budget && (
              <p id="broker-budget-error" className="text-red-600 text-sm mt-1" role="alert">
                {validationErrors.budget}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="broker-timeline" className="block text-sm font-semibold text-[#1C2B1E] mb-1.5">
              Timeline <span className="text-red-500">*</span>
            </label>
            <select
              id="broker-timeline"
              name="timeline"
              required
              aria-required="true"
              aria-invalid={!!validationErrors.timeline}
              aria-describedby={validationErrors.timeline ? 'broker-timeline-error' : undefined}
              className={`w-full border rounded-lg px-4 py-3 text-sm text-[#1C2B1E] focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition bg-white cursor-pointer ${
                validationErrors.timeline ? 'border-red-500' : 'border-[#E8E2D6]'
              }`}
            >
              <option value="">When do you need it?</option>
              {TIMELINES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {validationErrors.timeline && (
              <p id="broker-timeline-error" className="text-red-600 text-sm mt-1" role="alert">
                {validationErrors.timeline}
              </p>
            )}
          </div>
        </div>

        {/* Additional Details */}
        <div>
          <label htmlFor="broker-details" className="block text-sm font-semibold text-[#1C2B1E] mb-1.5">
            Additional Details
            <span className="ml-2 text-xs font-normal text-[#4A5568]" id="broker-details-hint">({charCount}/500 characters)</span>
          </label>
          <textarea
            id="broker-details"
            name="vehicle_details"
            rows={4}
            maxLength={500}
            onChange={(e) => setCharCount(e.target.value.length)}
            aria-invalid={!!validationErrors.vehicle_details}
            aria-describedby={`broker-details-hint ${validationErrors.vehicle_details ? 'broker-details-error' : ''}`}
            placeholder="Describe any must-have features, color preferences, max mileage, trim level, or anything else that matters to you..."
            className={`w-full border rounded-lg px-4 py-3 text-sm text-[#1C2B1E] placeholder-gray-400 focus:outline-none focus:border-[#E8B84B] focus:ring-1 focus:ring-[#E8B84B] transition resize-none ${
              validationErrors.vehicle_details ? 'border-red-500' : 'border-[#E8E2D6]'
            }`}
          />
          {validationErrors.vehicle_details && (
            <p id="broker-details-error" className="text-red-600 text-sm mt-1" role="alert">
              {validationErrors.vehicle_details}
            </p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={status === 'submitting' || charCount > 500}
          className="w-full bg-[#E8B84B] text-[#1C2B1E] py-4 rounded-lg font-bold text-base hover:bg-[#d4a43a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
        >
          {status === 'submitting' ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-[#1C2B1E] border-t-transparent rounded-full animate-spin"></span>
              Sending Request...
            </>
          ) : (
            <>
              <i className="ri-search-line text-lg"></i>
              Start My Vehicle Search
            </>
          )}
        </button>

        {/* What Happens Next */}
        <div className="border-t border-[#E8E2D6] pt-6">
          <p className="text-sm font-semibold text-[#1C2B1E] mb-3">What happens after I submit?</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-xs text-[#4A5568]">
            <div className="flex items-start gap-2">
              <i className="ri-check-line text-[#97BC62] text-base mt-0.5 flex-shrink-0"></i>
              <span>We review your request (same day)</span>
            </div>
            <span className="hidden sm:inline text-[#E8E2D6]">→</span>
            <div className="flex items-start gap-2">
              <i className="ri-check-line text-[#97BC62] text-base mt-0.5 flex-shrink-0"></i>
              <span>We call or email you to confirm details (within 1 business day)</span>
            </div>
            <span className="hidden sm:inline text-[#E8E2D6]">→</span>
            <div className="flex items-start gap-2">
              <i className="ri-check-line text-[#97BC62] text-base mt-0.5 flex-shrink-0"></i>
              <span>We start the search — no payment until you say go</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}